import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useBulkMessagingAcls } from '@frontend/bulk-messaging';
import { useTranslation } from '@frontend/i18n';
import { useGetWeaveInsuranceVerificationCustomizationFlagDetails } from '@frontend/insurance-verification';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { SettingsRoutes } from '@frontend/settings-routing';

export type AllPathsMap = Record<SettingsRoutes, { label: string; show?: boolean }>;
type NavItemsPaths = SettingsRoutes;

type NavItem = {
  label: string;
  path: NavItemsPaths;
  show?: boolean;
  hasBottomSpacing?: boolean;
  description?: string;
};

function useSettingsNavItems() {
  const { t } = useTranslation('settingsNav');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { selectedLocationIds } = useAppScopeStore();
  const hasContactSettingsAccess = getFeatureFlagValue('contact-settings-v2');
  const { isFeatureHiddenInAllLocation, isDental } = useGetWeaveInsuranceVerificationCustomizationFlagDetails();
  const hasAccessToInsuranceVerification = selectedLocationIds.some((locationId) =>
    hasSchemaACL(locationId, [Permission.INSURANCE_VERIFICATION_READ, Permission.INSURANCE_VERIFICATION_WRITE])
  );
  const { hasBulkMessagingAccess } = useBulkMessagingAcls();

  const navItems: NavItem[] = [
    {
      label: t('Tags') /* AUTO-GENERATED */,
      path: '/tags',
      hasBottomSpacing: true,
    },
    {
      label: t('Contacts') /* AUTO-GENERATED */,
      path: '/contacts',
      show: hasContactSettingsAccess,
    },
    {
      label: t('Email') /* AUTO-GENERATED */,
      path: '/email',
      description:
        'Email marketing settings. Manage email addresses and domains used with email marketing. Send legacy email campaigns.',
      show: hasBulkMessagingAccess,
    },
    {
      label: t('Fax') /* AUTO-GENERATED */,
      path: '/fax',
      description: 'Primary fax module overview, providing quick access to inbox, drafts, and fax configuration.',
    },
    {
      label: t('Forms') /* AUTO-GENERATED */,
      path: '/digital-forms',
      description:
        'Shortcut to the digital forms module. View, create, or manage practice forms in a centralized place.',
    },
    {
      label: t('Insurance'),
      path: '/insurance',
      show: isDental && !isFeatureHiddenInAllLocation && hasAccessToInsuranceVerification,
    },
    {
      label: t('Messaging') /* AUTO-GENERATED */,
      path: '/messaging',
      description:
        'Primary messaging module overview. May contain shortcuts to inbox, archived messages, and templates.',
    },
    {
      label: t('Payments') /* AUTO-GENERATED */,
      path: '/payments',
      description: 'Main payments overview page. Access invoices, statements, settings, and payment-related features.',
    },
    { label: t('Phone'), path: '/phone/main' },
    {
      label: t('Social') /* AUTO-GENERATED */,
      path: '/reviews',
    },
    {
      label: t('Schedule') /* AUTO-GENERATED */,
      path: '/schedule',
      hasBottomSpacing: true,
      description: 'Primary scheduling hub, linking to calendar, appointment',
    },
    { label: t('Image Gallery') /* AUTO-GENERATED */, path: '/image-gallery' },
    { label: t('Subscriptions') /* AUTO-GENERATED */, path: '/subscriptions' },
  ];

  return navItems;
}

export function useHidePickerConditions(): ((matches: { pathname: string; id: string }[]) => boolean)[] {
  return [];
}

export { useSettingsNavItems };
